import { PageResponse } from '@nebula-shared/data-access';

export type EstadoEntidadBancaria = 'ACTIVE' | 'INACTIVE' | 'DELETED';

export type EstadoCuentaBancariaApi = 'ACTIVO' | 'INACTIVO';

export type TipoCuentaBancaria = 'AHORRO' | 'CORRIENTE' | 'INVERSION' | 'DIGITAL' | 'CRUCE';

export type SobregiroFlag = 'S' | 'N';

export interface EntidadBancariaListado {
  id: number;
  /** Alias UI de codCuenta. */
  codigo: string;
  nombre: string;
  numeroCuenta?: string | null;
  sobregiro?: string;
  /** Opera chequera. API create/update: S|N; listado puede omitirlo. */
  operaChequera?: boolean | SobregiroFlag | string | null;
  chequera?: SobregiroFlag | string | null;
  consecutivoCheque?: number | null;
  fechaCierre?: string | null;
  saldoMinimo?: number | null;
  estadoEntidadBancaria: EstadoEntidadBancaria;
  tipoCuenta?: TipoCuentaBancaria | string | null;
  /** Código nacional banco (create). */
  codigoNacionalBanco?: string | null;
  terceroId?: number | null;
  terceroIdentificacion?: string;
  terceroNombre?: string;
  contactoNombre?: string | null;
  contactoCargo?: string | null;
  contactoTelefono?: string | null;
  contactoEmail?: string | null;
  fuenteFinanciacionId?: number | null;
  fuenteFinanciacionCodigo?: string | null;
  fuenteFinanciacionNombre?: string | null;
  cuentaContableId?: number | null;
  cuentaContableCodigo?: string | null;
  cuentaContableNombre?: string | null;
  /** Flag del back (HTU). Si llega false, oculta eliminar. */
  puedeEliminar?: boolean;
  createdAt?: string;
  createdBy?: string;
  version?: number;
}

/** Detalle de consulta/edición. */
export type EntidadBancariaDetalle = EntidadBancariaListado;

/**
 * Payload create/update — contrato HTU `CuentaBancariaDto`
 * (POST/PATCH `/cuentas-bancarias`).
 */
export interface CreateEntidadBancariaPayload {
  codigoNacionalBanco: string;
  numeroCuenta: string;
  nombreCuenta: string;
  tipoCuenta: string;
  estadoCuenta: EstadoCuentaBancariaApi;
  chequera?: SobregiroFlag;
  consecutivoChequera?: string | null;
  fuenteFinanciacionCodigo?: string | null;
  contactoNombre?: string | null;
  contactoCargo?: string | null;
  contactoNumero?: string | null;
  cuentaContableId?: number | null;
}

/** Payload update (PATCH) — mismo DTO; identidad la ignora el back. */
export type UpdateEntidadBancariaPayload = CreateEntidadBancariaPayload;

export type PageResponseEntidadBancaria = PageResponse<EntidadBancariaListado>;

/** Item crudo del listado HTU. */
export interface CuentaBancariaListadoApi {
  id: number;
  codCuenta?: string;
  numeroCuenta?: string;
  nombreCuenta?: string;
  estadoCuenta?: string;
  noIdentificacion?: string;
  banco?: string;
  fuenteFinanciacion?: string;
  puedeEliminar?: boolean;
}

/** Detalle crudo HTU (read/create/update response). */
export interface CuentaBancariaResponseApi extends CuentaBancariaListadoApi {
  codigoNacionalBanco?: string;
  tipoCuenta?: string;
  sobregiro?: string;
  chequera?: string | boolean;
  consecutivoChequera?: string | number | null;
  contactoNombre?: string | null;
  contactoCargo?: string | null;
  contactoNumero?: string | null;
  contactoTelefono?: string | null;
  fuenteFinanciacionCodigo?: string | null;
  cuentaContableId?: number | null;
  cuentaContableCodigo?: string | null;
  cuentaContableNombre?: string | null;
  terceroId?: number | null;
  version?: number;
}

/** Respuesta buscador códigos nacionales. */
export interface CodigoNacionalResponse {
  terceroId?: number;
  codigoNacional: string;
  noIdentificacion?: string;
  nombreBanco?: string;
  /** Alias tolerados por despliegues anteriores. */
  codigoSupBancaria?: string;
  numeroDocumento?: string;
  codigo?: string;
  nombre?: string;
  id?: number;
}

/** Respuesta buscador cuentas contables (movimiento). */
export interface CuentaMovimientoResponse {
  id: number;
  codigo: string;
  nombre: string;
}

/** @deprecated Saldo ya no se consulta; Back valida sobregiro en update. */
export interface SaldoEntidadBancaria {
  codigo?: string;
  saldo?: number | null;
}

export function mapEstadoApiToUi(estado?: string | null): EstadoEntidadBancaria {
  const raw = String(estado ?? '')
    .trim()
    .toUpperCase();
  if (raw === 'ACTIVO' || raw === 'ACTIVE') return 'ACTIVE';
  if (raw === 'INACTIVO' || raw === 'INACTIVE') return 'INACTIVE';
  if (raw === 'DELETED' || raw === 'ELIMINADO') return 'DELETED';
  return 'ACTIVE';
}

export function mapEstadoUiToApi(estado?: string | null): EstadoCuentaBancariaApi {
  const raw = String(estado ?? '')
    .trim()
    .toUpperCase();
  if (raw === 'INACTIVO' || raw === 'INACTIVE') return 'INACTIVO';
  return 'ACTIVO';
}

/** Extrae cód. nacional y tipo desde `codCuenta` (ej. 001-CO-1234). */
export function parseCodCuentaParts(codCuenta?: string | null): {
  codigoNacional?: string;
  tipoCuenta?: TipoCuentaBancaria;
} {
  const parts = String(codCuenta ?? '')
    .trim()
    .toUpperCase()
    .split('-')
    .filter(Boolean);
  if (parts.length < 2) return {};
  const tipoMap: Record<string, TipoCuentaBancaria> = {
    AH: 'AHORRO',
    CO: 'CORRIENTE',
    IN: 'INVERSION',
    DI: 'DIGITAL',
    CR: 'CRUCE',
  };
  return {
    codigoNacional: parts[0]?.slice(0, 3),
    tipoCuenta: tipoMap[parts[1]] ?? undefined,
  };
}

/** Normaliza listado/detalle API → modelo UI. */
export function mapCuentaBancariaApiToUi(
  raw: CuentaBancariaResponseApi | CuentaBancariaListadoApi | null | undefined,
): EntidadBancariaListado {
  const r = (raw ?? {}) as CuentaBancariaResponseApi;
  const chequera = r.chequera;
  const consec =
    r.consecutivoChequera === null || r.consecutivoChequera === undefined
      ? null
      : Number(r.consecutivoChequera);
  const codCuenta = String(r.codCuenta ?? '');
  const parsed = parseCodCuentaParts(codCuenta);

  return {
    id: Number(r.id) || 0,
    codigo: codCuenta,
    nombre: String(r.nombreCuenta ?? ''),
    numeroCuenta: r.numeroCuenta ?? null,
    estadoEntidadBancaria: mapEstadoApiToUi(r.estadoCuenta),
    terceroIdentificacion: String(r.noIdentificacion ?? ''),
    terceroNombre: String(r.banco ?? ''),
    fuenteFinanciacionNombre: r.fuenteFinanciacion ?? r.fuenteFinanciacionCodigo ?? null,
    fuenteFinanciacionCodigo: r.fuenteFinanciacionCodigo ?? r.fuenteFinanciacion ?? null,
    puedeEliminar: r.puedeEliminar,
    codigoNacionalBanco: r.codigoNacionalBanco ?? parsed.codigoNacional ?? null,
    tipoCuenta:
      (r.tipoCuenta as TipoCuentaBancaria | null | undefined) ?? parsed.tipoCuenta ?? null,
    sobregiro: r.sobregiro ?? 'N',
    chequera: typeof chequera === 'boolean' ? (chequera ? 'S' : 'N') : (chequera as SobregiroFlag),
    operaChequera: chequera,
    consecutivoCheque: Number.isFinite(consec as number) ? (consec as number) : null,
    contactoNombre: r.contactoNombre ?? null,
    contactoCargo: r.contactoCargo ?? null,
    contactoTelefono: r.contactoNumero ?? r.contactoTelefono ?? null,
    cuentaContableId: r.cuentaContableId ?? null,
    cuentaContableCodigo: r.cuentaContableCodigo ?? null,
    cuentaContableNombre: r.cuentaContableNombre ?? null,
    terceroId: r.terceroId ?? null,
    version: r.version,
  };
}

/** Une detalle API + fila de listado sin perder labels/campos parciales. */
export function mergeCuentaBancariaDetalle(
  detalle: EntidadBancariaListado | null | undefined,
  row: EntidadBancariaListado,
): EntidadBancariaListado {
  const d = detalle ?? ({} as EntidadBancariaListado);
  const pick = <T>(
    primary: T | null | undefined,
    fallback: T | null | undefined,
  ): T | null | undefined => {
    if (primary === null || primary === undefined) return fallback;
    if (typeof primary === 'string' && primary.trim() === '') return fallback;
    return primary;
  };

  const codigo = String(pick(d.codigo, row.codigo) ?? '');
  const parsed = parseCodCuentaParts(codigo);

  return {
    ...row,
    ...d,
    id: d.id || row.id,
    codigo,
    nombre: String(pick(d.nombre, row.nombre) ?? ''),
    numeroCuenta: pick(d.numeroCuenta, row.numeroCuenta) ?? null,
    estadoEntidadBancaria: d.estadoEntidadBancaria || row.estadoEntidadBancaria,
    terceroId: pick(d.terceroId, row.terceroId) ?? null,
    terceroIdentificacion: String(pick(d.terceroIdentificacion, row.terceroIdentificacion) ?? ''),
    terceroNombre: String(pick(d.terceroNombre, row.terceroNombre) ?? ''),
    codigoNacionalBanco:
      pick(d.codigoNacionalBanco, row.codigoNacionalBanco) ?? parsed.codigoNacional ?? null,
    tipoCuenta: pick(d.tipoCuenta, row.tipoCuenta) ?? parsed.tipoCuenta ?? null,
    sobregiro: pick(d.sobregiro, row.sobregiro) ?? 'N',
    chequera: pick(d.chequera, row.chequera) ?? null,
    operaChequera: pick(d.operaChequera, row.operaChequera) ?? null,
    consecutivoCheque: pick(d.consecutivoCheque, row.consecutivoCheque) ?? null,
    contactoNombre: pick(d.contactoNombre, row.contactoNombre) ?? null,
    contactoCargo: pick(d.contactoCargo, row.contactoCargo) ?? null,
    contactoTelefono: pick(d.contactoTelefono, row.contactoTelefono) ?? null,
    fuenteFinanciacionCodigo:
      pick(d.fuenteFinanciacionCodigo, row.fuenteFinanciacionCodigo) ?? null,
    fuenteFinanciacionNombre:
      pick(d.fuenteFinanciacionNombre, row.fuenteFinanciacionNombre) ?? null,
    cuentaContableId: pick(d.cuentaContableId, row.cuentaContableId) ?? null,
    cuentaContableCodigo: pick(d.cuentaContableCodigo, row.cuentaContableCodigo) ?? null,
    cuentaContableNombre: pick(d.cuentaContableNombre, row.cuentaContableNombre) ?? null,
    puedeEliminar: d.puedeEliminar ?? row.puedeEliminar,
    version: d.version ?? row.version,
  };
}
